import {
  Band,
  Button,
  Eyebrow,
  Hero,
  Prose,
  Split,
  Wrap,
  promises,
  sectionHead,
} from "../components/ui/index.ts";

/**
 * Everything that is only true of logic grids.
 *
 * The front page carries the press's standard — we check what we print — and
 * nothing narrower, because "exactly one solution", "reachable without
 * guessing" and "minimal clue lists" are claims about *this* puzzle type. A
 * second puzzle type cannot make them and should not be forced to. Keeping them
 * here means a new line can arrive with its own guarantees and its own page,
 * without anybody having to unpick a promise the company appeared to make.
 */
export default function LogicGrids() {
  return (
    <>
      <Hero
        eyebrow="The line"
        title="Logic grid puzzles."
        lede="Match a few people to a few things using a list of clues. You cross out what can't be true and keep going until only one arrangement is left."
        emblem="default"
        actions={
          <>
            <Button to="/catalogue">See the books</Button>
            <Button to="/help" ghost>
              Stuck on one?
            </Button>
          </>
        }
      />

      <Band id="guarantee">
        <Wrap>
          <Eyebrow>What we guarantee</Eyebrow>
          <h2 className={sectionHead}>Three promises we can actually check.</h2>

          <div className={promises}>
            <div className="border-t-2 border-rust pt-[0.9rem]">
              <h3 className="font-display font-bold m-0 mb-[0.35rem]">Exactly one solution</h3>
              <p className="text-muted text-minor m-0">
                Every puzzle gets solved exhaustively before it ships. If there's a second
                arrangement that fits the clues, we throw it out and make another.
              </p>
            </div>
            <div className="border-t-2 border-rust pt-[0.9rem]">
              <h3 className="font-display font-bold m-0 mb-[0.35rem]">No guessing, ever</h3>
              <p className="text-muted text-minor m-0">
                You can reach the answer by deduction from the first clue to the last. If our solver
                has to guess and backtrack, so would you, and we don't ship those.
              </p>
            </div>
            <div className="border-t-2 border-rust pt-[0.9rem]">
              <h3 className="font-display font-bold m-0 mb-[0.35rem]">No padding</h3>
              <p className="text-muted text-minor m-0">
                Every clue in the list is doing work. Take any one of them out and the puzzle stops
                having a single answer.
              </p>
            </div>
          </div>
        </Wrap>
      </Band>

      <Band variant="alt" id="how">
        <Wrap>
          <Eyebrow>How they are made</Eyebrow>
          <h2 className={sectionHead}>Generated, then proved.</h2>
          <Split spaced>
            <Prose>
              <p className="lead-para">
                Lots of puzzle books ship grids with two valid answers, or ones you can only crack
                by guessing and backing up when it goes wrong. Either way you've stopped solving and
                started grinding.
              </p>
              <p>
                So we do it backwards. We generate a puzzle, then have a machine solve it using only
                the techniques the book has taught you by that point. It ships if it passes all
                three checks above, and most don't.
              </p>
            </Prose>
            <Prose>
              <h3 className="font-display font-bold mt-0">
                Difficulty means more puzzle, not less help
              </h3>
              <p>
                A harder tier means bigger grids, more categories, and more kinds of clue in play.
                It never means we've held a technique back to slow you down.
              </p>
              <p>
                The ladder goes Introduction, Easy, Medium, Hard and Expert, then three more tiers
                for people who want a proper fight.
              </p>
            </Prose>
          </Split>
        </Wrap>
      </Band>

      <Band id="stuck">
        <Wrap>
          <Eyebrow>If you get stuck</Eyebrow>
          <h2 className={sectionHead}>There is always another deduction.</h2>
          <Prose>
            <p>
              That's what the guarantee buys you: when a puzzle stops moving, the next step is there
              and you already have what you need to find it. Nine times out of ten something got
              marked wrong twenty minutes back.
            </p>
            <p>
              Every book has the full answer key in the back. If that's more than you want, tell us
              how far you've got and we'll give you just the next step.
            </p>
            <p>
              <Button to="/help">Get a hint</Button>
            </p>
          </Prose>
        </Wrap>
      </Band>
    </>
  );
}
